import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [animatedStats, setAnimatedStats] = useState({
    experience: 0,
    portfolio: 0,
    investment: 0
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const animateStats = () => {
      const targets = { experience: 5, portfolio: 8, investment: 0.8 };
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setAnimatedStats({
          experience: Math.floor(targets?.experience * progress),
          portfolio: Math.floor(targets?.portfolio * progress),
          investment: Math.floor(targets?.investment * progress * 10) / 10
        });

        if (currentStep >= steps) {
          clearInterval(timer);
          setAnimatedStats(targets);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    };

    const timer = setTimeout(animateStats, 500);
    return () => clearTimeout(timer);
  }, []);

  const content = {
    en: {
      headline: "Strategic Technology Meets Digital Innovation",
      subheadline: "Empowering European startups with capital, expertise, and technology to scale globally. From Hamburg to the world.",
      cta1: "Explore Portfolio",
      cta2: "Schedule Meeting",
      stats: {
        experience: "Years Experience",
        portfolio: "Portfolio Companies",
        investment: "Million Invested"
      },
      trustBadges: ["Hamburg Chamber of Commerce", "EU Regulated", "GDPR Compliant"]
    },
    de: {
      headline: "Strategische Technologie trifft digitale Innovation",
      subheadline: "Europäische Startups mit Kapital, Expertise und Technologie für globales Wachstum stärken. Von Hamburg in die Welt.",
      cta1: "Portfolio erkunden",
      cta2: "Termin vereinbaren",
      stats: {
        experience: "Jahre Erfahrung",
        portfolio: "Portfolio-Unternehmen",
        investment: "Millionen investiert"
      },
      trustBadges: ["Handelskammer Hamburg", "EU-reguliert", "DSGVO-konform"]
    },
    fr: {
      headline: "La technologie stratégique rencontre l\'innovation numérique",
      subheadline: "Autonomiser les startups européennes avec du capital, de l'expertise et de la technologie pour une croissance mondiale. De Hambourg vers le monde.",
      cta1: "Explorer le Portfolio",
      cta2: "Planifier une Réunion",
      stats: {
        experience: "Années d\'Expérience",
        portfolio: "Entreprises du Portfolio",
        investment: "Millions Investis"
      },
      trustBadges: ["Chambre de Commerce de Hambourg", "Réglementé UE", "Conforme RGPD"]
    },
    es: {
      headline: "La tecnología estratégica se encuentra con la innovación digital",
      subheadline: "Empoderando startups europeas con capital, experiencia y tecnología para escalar globalmente. Desde Hamburgo al mundo.",
      cta1: "Explorar Portfolio",
      cta2: "Programar Reunión",
      stats: {
        experience: "Años de Experiencia",
        portfolio: "Empresas del Portfolio",
        investment: "Millones Invertidos"
      },
      trustBadges: ["Cámara de Comercio de Hamburgo", "Regulado UE", "Cumple GDPR"]
    }
  };

  const currentContent = content?.[currentLanguage];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-card to-muted">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 morphing-gradient opacity-10"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8 scroll-reveal">
            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-3 mb-6">
              {currentContent?.trustBadges?.map((badge, index) =>
              <div
                key={index}
                className={`px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full border border-primary/20 stagger-${index + 1}`}>

                  {badge}
                </div>
              )}
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-gradient-brand">{currentContent?.headline?.split(' ')?.slice(0, 2)?.join(' ')}</span>
                <br />
                <span className="text-foreground">{currentContent?.headline?.split(' ')?.slice(2)?.join(' ')}</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                {currentContent?.subheadline}
              </p>
            </div>

            {/* Animated Statistics */}
            <div className="grid grid-cols-3 gap-6 py-8">
              <div className="text-center space-y-2 counter-animate">
                <div className="text-3xl md:text-4xl font-bold text-primary">
                  {animatedStats?.experience}+
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {currentContent?.stats?.experience}
                </div>
              </div>
              <div className="text-center space-y-2 counter-animate stagger-2">
                <div className="text-3xl md:text-4xl font-bold text-accent">
                  {animatedStats?.portfolio}+
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {currentContent?.stats?.portfolio}
                </div>
              </div>
              <div className="text-center space-y-2 counter-animate stagger-3">
                <div className="text-3xl md:text-4xl font-bold text-success">
                  €{animatedStats?.investment}M+
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {currentContent?.stats?.investment}
                </div>
              </div>
            </div>

            {/* Call to Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 pulse-cta"
                iconName="ArrowRight"
                iconPosition="right">

                <Link to="/portfolio" className="flex items-center space-x-2">
                  <span>{currentContent?.cta1}</span>
                </Link>
              </Button>
            </div>

            {/* Social Proof Indicators */}
            <div className="flex items-center space-x-6 pt-4">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="MapPin" size={16} />
                <span>Hamburg, Germany</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Globe" size={16} />
                <span>Global Reach</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-success">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>Active Investing</span>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            {/* Main Hero Image */}
            <div className="relative z-10 parallax-card">
              <div className="relative overflow-hidden rounded-brand shadow-brand-lg">
                <Image
                  src="https://images.unsplash.com/photo-1624002534843-065785c2571d"
                  alt="Modern Hamburg office building with glass facade reflecting blue sky, representing REXFEL's professional headquarters"
                  className="w-full h-96 object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
              </div>
            </div>

            {/* Business Skyscrapers Image Section */}
            <div className="relative mt-6 z-10">
              <div className="relative overflow-hidden rounded-brand shadow-brand">
                <Image
                  src="/assets/images/Screenshot_2025-10-21_at_17.05.42-1761059145475.png"
                  alt="Business and financial interface mockups showing portfolio growth metrics and professional dashboard design with modern cards and financial excellence indicators"
                  className="w-full h-64 object-cover opacity-90" />
                
                <div className="absolute inset-0 bg-gradient-to-r from-accent/30 to-primary/30"></div>
                
                {/* Overlay text for context */}
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="text-sm font-medium bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                    Technology and Innovation
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Stats Cards */}
            <div className="absolute -top-6 -left-6 z-20">
              <div className="bg-card border border-border rounded-brand p-4 shadow-brand floating-widget">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Portfolio Growth</div>
                    <div className="text-lg font-bold text-success">+127%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-6 z-20">
              <div className="bg-card border border-border rounded-brand p-4 shadow-brand floating-widget">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Active Founders</div>
                    <div className="text-lg font-bold text-primary">8</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Decorative Elements */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary/10 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center space-y-2 text-muted-foreground">
          <span className="text-xs font-medium">Scroll to explore</span>
          <Icon name="ChevronDown" size={20} />
        </div>
      </div>
    </section>);

};

export default HeroSection;